// ============================================================================
// Facebook Page — publish / schedule / cancelScheduled / deletePost.
//
// Endpoints used:
//   - POST /{page-id}/feed                — text / link / multi-photo posts
//   - POST /{page-id}/photos              — single-photo post when no other
//                                            media is attached
//   - DELETE /{post-id}                   — both scheduled and published
//
// Implementation rules:
//   - Page Access Token is the credential. Sent via Authorization: Bearer so
//     it never appears in URL/edge logs.
//   - Every outbound call goes through `runRateLimited`, which gates against
//     the connectors rate-limiter DO.
//   - Every error path returns `SocialResult.error` — we NEVER throw across
//     the capability boundary.
//   - Idempotency: when the caller supplies `args.idempotencyKey` we look up
//     the (instanceId, key) entry from the KV cache; on hit we echo the
//     previous result without calling Meta.
//
// Provider-side scheduling window: 10min ≤ scheduledFor ≤ 6mo from now. We
// validate up-front so we can return a clean `provider_rejected` without
// burning a Meta API call.
// ============================================================================

import type {
  SocialCancelScheduledArgs,
  SocialCancelScheduledResult,
  SocialDeleteArgs,
  SocialDeleteResult,
  SocialPostingCallCtx,
  SocialPostingError,
  SocialPublishArgs,
  SocialPublishResult,
  SocialResult,
  SocialScheduleArgs,
  SocialScheduleResult,
  SocialUpdatePostArgs,
  SocialUpdatePostResult,
} from '../../capabilities/social-posting'
import type { RateLimitCheck } from './rate-limit-check'
import { errorFromResponse, sanitiseMetaErrorString } from './errors'
import { FB_PAGE_GRAPH_API_BASE } from './oauth'
import type {
  FacebookPageDeleteResponse,
  FacebookPageIdempotencyCacheEntry,
  FacebookPagePublishResponse,
} from './types'

// ============================================================================
// Constants
// ============================================================================

const SCHEDULE_MIN_OFFSET_SECONDS = 10 * 60 // 10 minutes
const SCHEDULE_MAX_OFFSET_SECONDS = 6 * 30 * 24 * 60 * 60 // ~6 months (180 days)

/**
 * Endpoint slug used for rate-limiter accounting. Marketing post traffic is
 * orders of magnitude lower than messaging — single bucket per instance is
 * enough for v1. (#790 will tune buckets per-endpoint.)
 */
const ENDPOINT_LABEL = 'facebook:page_publish'

const IDEMPOTENCY_TTL_SECONDS = 60 * 60 * 24 // 24h

// ============================================================================
// Shared deps
// ============================================================================

export interface FacebookPagePublisherDeps {
  fetchImpl?: typeof fetch
  graphApiBase?: string
  /**
   * Optional KV namespace for idempotency cache. When omitted, idempotency
   * checks short-circuit (always treated as "first call"). Marketing should
   * configure this in production.
   */
  idempotencyCache?: IdempotencyCache
  /** Provide current time — tests inject a fixed clock. */
  now?: () => Date
  /**
   * Rate-limit check callback. When omitted, the adapter fail-opens (passes
   * every request through). The dispatcher / registration site wires a real
   * check that delegates to `services/rate-limiter.service`.
   */
  rateLimitCheck?: RateLimitCheck
}

/**
 * Minimal interface adapter consumers can implement to back the dedupe cache.
 * In production this is a Cloudflare KV namespace; in tests a Map.
 */
export interface IdempotencyCache {
  get(key: string): Promise<string | null>
  put(key: string, value: string, opts?: { expirationTtl?: number }): Promise<void>
}

// ============================================================================
// publish
// ============================================================================

export async function publish(
  args: SocialPublishArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPagePublisherDeps = {},
): Promise<SocialResult<SocialPublishResult>> {
  return publishInternal(args, pageAccessToken, ctx, deps, { mode: 'live' })
}

export async function schedule(
  args: SocialScheduleArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPagePublisherDeps = {},
): Promise<SocialResult<SocialScheduleResult>> {
  const validation = validateScheduledTime(args.scheduledFor, deps.now?.() ?? new Date())
  if (!validation.ok) {
    return { ok: false, error: validation.error }
  }

  const result = await publishInternal(args, pageAccessToken, ctx, deps, {
    mode: 'scheduled',
    scheduledForUnix: validation.unix,
  })
  if (!result.ok) {
    return { ok: false, error: result.error }
  }

  return {
    ok: true,
    data: {
      providerPostId: result.data.providerPostId,
      scheduledFor: args.scheduledFor,
      appliedAccountId: result.data.appliedAccountId,
      idempotencyKey: result.data.idempotencyKey,
    },
  }
}

// ============================================================================
// publishInternal — shared body for live + scheduled posts
// ============================================================================

interface PublishMode {
  mode: 'live' | 'scheduled'
  scheduledForUnix?: number
}

async function publishInternal(
  args: SocialPublishArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPagePublisherDeps,
  mode: PublishMode,
): Promise<SocialResult<SocialPublishResult>> {
  if (typeof pageAccessToken !== 'string' || pageAccessToken.length === 0) {
    return tokenError()
  }

  if (typeof args.targetAccountId !== 'string' || args.targetAccountId.length === 0) {
    return validationError('targetAccountId is required')
  }

  // --- Idempotency cache short-circuit ----------------------------------
  if (args.idempotencyKey && deps.idempotencyCache) {
    const cached = await readIdempotencyCache(
      deps.idempotencyCache,
      ctx.instanceId,
      args.idempotencyKey,
    )
    if (cached) {
      ctx.redactedLogger.info('publish: idempotency cache hit', {
        instanceId: ctx.instanceId,
        idempotencyKey: args.idempotencyKey,
      })
      return {
        ok: true,
        data: {
          providerPostId: cached.providerPostId,
          providerUrl: cached.providerUrl,
          publishedAt: cached.publishedAt,
          appliedAccountId: cached.appliedAccountId,
          idempotencyKey: args.idempotencyKey,
        },
      }
    }
  }

  // --- Rate limiter -----------------------------------------------------
  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<SocialPublishResult>

  // --- Build the request body -------------------------------------------
  const fetchImpl = deps.fetchImpl ?? fetch
  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE

  // Decide endpoint: `/photos` for single-image-no-other-content; `/feed`
  // for text-only, link, or multi-media.
  const route = decidePublishRoute(args)

  const requestUrl = `${base}/${encodeURIComponent(args.targetAccountId)}/${route}`
  const formBody = buildPublishBody(args, mode, route)

  let response: Response
  try {
    response = await fetchImpl(requestUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${pageAccessToken}`,
      },
      body: formBody.toString(),
    })
  } catch (err) {
    ctx.redactedLogger.error('publish network error', {
      error: err instanceof Error ? err.message : 'unknown',
    })
    return {
      ok: false,
      error: {
        code: 'transient',
        message: 'Network error talking to Facebook Graph',
        retryable: true,
      },
    }
  }

  if (!response.ok) {
    const err = await errorFromResponse(response, 'Facebook Graph publish failed')
    ctx.redactedLogger.warn('publish failed', {
      code: err.code,
      httpStatus: response.status,
      providerTraceId: err.providerTraceId,
    })
    return { ok: false, error: err }
  }

  const body = (await response.json().catch(() => null)) as FacebookPagePublishResponse | null
  if (!body || typeof body.id !== 'string' || body.id.length === 0) {
    return {
      ok: false,
      error: {
        code: 'unknown',
        message: 'Facebook Graph returned no post id',
        retryable: false,
      },
    }
  }

  // `/feed` returns both `id` (combined `{page_id}_{post_id}`) and `post_id`.
  // `/photos` returns only `id`. The combined form is what every other API
  // (/insights, /comments, DELETE) expects, so prefer it when available.
  const providerPostId = body.id
  const now = (deps.now ?? (() => new Date()))()
  // For both live + scheduled we stamp `publishedAt` with the call time.
  // - Live post: Meta accepted + made it visible at ~now.
  // - Scheduled post: Meta accepted at ~now; the actual go-live time is
  //   `scheduledFor`. Marketing wraps this with the scheduled timestamp at
  //   the `schedule()` callsite.
  const publishedAt = now.toISOString()

  const result: SocialPublishResult = {
    providerPostId,
    providerUrl: buildPostUrl(providerPostId, args.targetAccountId),
    publishedAt,
    appliedAccountId: args.targetAccountId,
    idempotencyKey: args.idempotencyKey,
  }

  // --- Persist into idempotency cache -----------------------------------
  if (args.idempotencyKey && deps.idempotencyCache) {
    await writeIdempotencyCache(
      deps.idempotencyCache,
      ctx.instanceId,
      args.idempotencyKey,
      {
        providerPostId,
        appliedAccountId: args.targetAccountId,
        providerUrl: result.providerUrl,
        publishedAt,
        createdAt: now.toISOString(),
      },
    )
  }

  ctx.redactedLogger.info('publish ok', {
    instanceId: ctx.instanceId,
    providerPostId,
    mode: mode.mode,
  })
  return { ok: true, data: result }
}

// ============================================================================
// cancelScheduled + deletePost
// ============================================================================

/**
 * Cancel a scheduled post. Meta uses `DELETE /{post-id}` for both scheduled
 * and live posts — the difference is purely the `is_published` field on the
 * post (we don't need to check it; `DELETE` is idempotent).
 */
export async function cancelScheduled(
  args: SocialCancelScheduledArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPagePublisherDeps = {},
): Promise<SocialResult<SocialCancelScheduledResult>> {
  const deleted = await deleteByProviderPostId(
    args.providerPostId,
    args.targetAccountId,
    pageAccessToken,
    ctx,
    deps,
  )
  if (!deleted.ok) return deleted as SocialResult<SocialCancelScheduledResult>
  return {
    ok: true,
    data: {
      providerPostId: args.providerPostId,
      cancelledAt: deleted.data.deletedAt,
    },
  }
}

export async function deletePost(
  args: SocialDeleteArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPagePublisherDeps = {},
): Promise<SocialResult<SocialDeleteResult>> {
  return deleteByProviderPostId(
    args.providerPostId,
    args.targetAccountId,
    pageAccessToken,
    ctx,
    deps,
  )
}

export async function updatePost(
  args: SocialUpdatePostArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPagePublisherDeps = {},
): Promise<SocialResult<SocialUpdatePostResult>> {
  if (typeof pageAccessToken !== 'string' || pageAccessToken.length === 0) {
    return tokenError()
  }
  if (typeof args.providerPostId !== 'string' || args.providerPostId.length === 0) {
    return validationError('providerPostId is required')
  }
  if (typeof args.targetAccountId !== 'string' || args.targetAccountId.length === 0) {
    return validationError('targetAccountId is required')
  }

  const body = buildUpdateBody(args)
  if ([...body.keys()].length === 0) {
    return validationError('at least one update field is required')
  }

  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<SocialUpdatePostResult>

  const fetchImpl = deps.fetchImpl ?? fetch
  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE
  const url = `${base}/${encodeURIComponent(args.providerPostId)}`

  let response: Response
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${pageAccessToken}`,
      },
      body: body.toString(),
    })
  } catch (err) {
    ctx.redactedLogger.error('updatePost network error', {
      error: err instanceof Error ? err.message : 'unknown',
    })
    return {
      ok: false,
      error: {
        code: 'transient',
        message: 'Network error talking to Facebook Graph',
        retryable: true,
      },
    }
  }

  if (!response.ok) {
    const err = await errorFromResponse(response, 'Facebook Graph update failed')
    ctx.redactedLogger.warn('updatePost failed', {
      code: err.code,
      httpStatus: response.status,
      providerPostId: args.providerPostId,
    })
    return { ok: false, error: err }
  }

  const parsed = (await response.json().catch(() => null)) as { success?: boolean } | null
  if (!parsed || parsed.success !== true) {
    return {
      ok: false,
      error: {
        code: 'unknown',
        message: 'Facebook Graph update did not return success: true',
        retryable: false,
      },
    }
  }

  return {
    ok: true,
    data: {
      providerPostId: args.providerPostId,
      updatedAt: (deps.now ?? (() => new Date()))().toISOString(),
    },
  }
}

async function deleteByProviderPostId(
  providerPostId: string,
  targetAccountId: string,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPagePublisherDeps,
): Promise<SocialResult<SocialDeleteResult>> {
  if (typeof pageAccessToken !== 'string' || pageAccessToken.length === 0) {
    return tokenError()
  }
  if (typeof providerPostId !== 'string' || providerPostId.length === 0) {
    return validationError('providerPostId is required')
  }
  if (typeof targetAccountId !== 'string' || targetAccountId.length === 0) {
    return validationError('targetAccountId is required')
  }

  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<SocialDeleteResult>

  const fetchImpl = deps.fetchImpl ?? fetch
  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE
  const url = `${base}/${encodeURIComponent(providerPostId)}`

  let response: Response
  try {
    response = await fetchImpl(url, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${pageAccessToken}`,
      },
    })
  } catch (err) {
    ctx.redactedLogger.error('delete network error', {
      error: err instanceof Error ? err.message : 'unknown',
    })
    return {
      ok: false,
      error: {
        code: 'transient',
        message: 'Network error talking to Facebook Graph',
        retryable: true,
      },
    }
  }

  if (!response.ok) {
    const err = await errorFromResponse(response, 'Facebook Graph delete failed')
    ctx.redactedLogger.warn('delete failed', {
      code: err.code,
      httpStatus: response.status,
      providerPostId,
    })
    return { ok: false, error: err }
  }

  const body = (await response.json().catch(() => null)) as FacebookPageDeleteResponse | null
  if (!body || body.success !== true) {
    return {
      ok: false,
      error: {
        code: 'unknown',
        message: 'Facebook Graph DELETE did not return success: true',
        retryable: false,
      },
    }
  }

  const now = (deps.now ?? (() => new Date()))()
  return {
    ok: true,
    data: {
      providerPostId,
      deletedAt: now.toISOString(),
    },
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * For #788 `publish()` always uses `/feed`. The `/photos` endpoint is only
 * relevant during `uploadMedia()` (fresh image creation with
 * `published=false` returning a media_fbid). Pre-uploaded photo ids are
 * attached via `attached_media` on `/feed`, even for single-image posts.
 *
 * Returning a function-like enum keeps the door open for future routes
 * (`videos` for video reels, `live_videos` for streams) without touching
 * the caller.
 */
function decidePublishRoute(_args: SocialPublishArgs): 'feed' {
  return 'feed'
}

function buildPublishBody(
  args: SocialPublishArgs,
  mode: PublishMode,
  _route: 'feed',
): URLSearchParams {
  const params = new URLSearchParams()

  if (typeof args.caption === 'string' && args.caption.length > 0) {
    params.set('message', args.caption)
  }

  if (typeof args.linkUrl === 'string' && args.linkUrl.length > 0) {
    params.set('link', args.linkUrl)
  }

  if (Array.isArray(args.mediaIds) && args.mediaIds.length > 0) {
    // Meta's `/feed` accepts `attached_media` as an array of JSON objects of
    // the shape `[ { "media_fbid": "<id>" } ]`. URL-encoded form param;
    // multi-image carousels post identically. Single-image posts use the
    // same shape (Meta treats a 1-element array as a single attached photo).
    const attached = args.mediaIds.map((id) => JSON.stringify({ media_fbid: id }))
    params.set('attached_media', `[${attached.join(',')}]`)
  }

  if (mode.mode === 'scheduled' && typeof mode.scheduledForUnix === 'number') {
    params.set('scheduled_publish_time', String(mode.scheduledForUnix))
    params.set('published', 'false')
  }

  if (typeof args.idempotencyKey === 'string' && args.idempotencyKey.length > 0) {
    // Meta `client_token` dedupes within the API window. Combined with our
    // KV cache for the longer window.
    params.set('client_token', args.idempotencyKey)
  }

  // Provider-specific knobs (privacy, targeting). Adapters silently ignore
  // unknown keys per the contract — Meta will reject unknown fields with a
  // descriptive error.
  if (args.providerOptions && typeof args.providerOptions === 'object') {
    for (const [k, v] of Object.entries(args.providerOptions)) {
      if (typeof v === 'string') params.set(k, v)
      else if (typeof v === 'number' || typeof v === 'boolean') params.set(k, String(v))
    }
  }

  return params
}

function buildUpdateBody(args: SocialUpdatePostArgs): URLSearchParams {
  const params = new URLSearchParams()

  if (typeof args.caption === 'string') {
    params.set('message', args.caption)
  }
  if (typeof args.linkUrl === 'string' && args.linkUrl.length > 0) {
    params.set('link', args.linkUrl)
  }
  if (args.providerOptions && typeof args.providerOptions === 'object') {
    for (const [k, v] of Object.entries(args.providerOptions)) {
      if (typeof v === 'string') params.set(k, v)
      else if (typeof v === 'number' || typeof v === 'boolean') params.set(k, String(v))
    }
  }

  return params
}

function buildPostUrl(providerPostId: string, _targetAccountId: string): string {
  // FB post URLs use the combined `{page_id}_{post_id}` form. Both
  // `/feed` (which returns it) and `/photos` (which returns just the photo
  // id) generate viewable URLs via this prefix.
  return `https://www.facebook.com/${encodeURIComponent(providerPostId)}`
}

interface ValidScheduleResult {
  ok: true
  unix: number
}

interface InvalidScheduleResult {
  ok: false
  error: SocialPostingError
}

function validateScheduledTime(
  scheduledFor: string | undefined,
  now: Date,
): ValidScheduleResult | InvalidScheduleResult {
  if (typeof scheduledFor !== 'string' || scheduledFor.length === 0) {
    return invalidSchedule('scheduledFor is required (ISO 8601)')
  }
  const parsed = Date.parse(scheduledFor)
  if (!Number.isFinite(parsed)) {
    return invalidSchedule('scheduledFor is not a valid ISO 8601 timestamp')
  }
  const offsetSeconds = Math.round((parsed - now.getTime()) / 1000)
  if (offsetSeconds < SCHEDULE_MIN_OFFSET_SECONDS) {
    return invalidSchedule(
      `scheduledFor must be at least 10 minutes from now; got ${offsetSeconds}s offset`,
    )
  }
  if (offsetSeconds > SCHEDULE_MAX_OFFSET_SECONDS) {
    return invalidSchedule(
      `scheduledFor must be at most 6 months from now; got ${offsetSeconds}s offset`,
    )
  }
  return { ok: true, unix: Math.floor(parsed / 1000) }
}

function invalidSchedule(msg: string): InvalidScheduleResult {
  return {
    ok: false,
    error: {
      code: 'provider_rejected',
      message: `${msg}. Meta requires 10 minutes ≤ scheduledFor ≤ 6 months.`,
      retryable: false,
    },
  }
}

function tokenError(): SocialResult<never> {
  return {
    ok: false,
    error: {
      code: 'auth',
      message: 'Facebook Page access token is missing',
      retryable: false,
    },
  }
}

function validationError(message: string): SocialResult<never> {
  return {
    ok: false,
    error: {
      code: 'provider_rejected',
      message: sanitiseMetaErrorString(message),
      retryable: false,
    },
  }
}

// ============================================================================
// Rate-limiter gate
// ============================================================================

async function checkRateLimit(
  ctx: SocialPostingCallCtx,
  endpoint: string,
  rateLimitCheck: RateLimitCheck | undefined,
): Promise<SocialResult<true>> {
  if (!rateLimitCheck) {
    // No callback supplied → pass-through. Production registers a real one;
    // tests + dev safely skip. (See `rate-limit-check.ts`.)
    return { ok: true, data: true }
  }
  const decision = await rateLimitCheck({ instanceId: ctx.instanceId, endpoint }, ctx)
  if (decision.allowed) return { ok: true, data: true }
  return {
    ok: false,
    error: {
      code: 'rate_limit',
      message: 'Rate limit exceeded for facebook:page_publish',
      retryable: true,
      retryAfterSeconds: decision.retryAfterSeconds,
    },
  }
}

// ============================================================================
// Idempotency cache I/O
// ============================================================================

function idempotencyKey(instanceId: string, key: string): string {
  return `connectors:fb-page:idempotency:${instanceId}:${key}`
}

async function readIdempotencyCache(
  cache: IdempotencyCache,
  instanceId: string,
  key: string,
): Promise<FacebookPageIdempotencyCacheEntry | null> {
  try {
    const raw = await cache.get(idempotencyKey(instanceId, key))
    if (!raw) return null
    return JSON.parse(raw) as FacebookPageIdempotencyCacheEntry
  } catch {
    return null
  }
}

async function writeIdempotencyCache(
  cache: IdempotencyCache,
  instanceId: string,
  key: string,
  entry: FacebookPageIdempotencyCacheEntry,
): Promise<void> {
  try {
    await cache.put(idempotencyKey(instanceId, key), JSON.stringify(entry), {
      expirationTtl: IDEMPOTENCY_TTL_SECONDS,
    })
  } catch {
    // Best-effort: a cache miss next time just means we send the same
    // client_token to Meta and rely on its dedupe window.
  }
}
